import TextWithLineBreaks from '@/components/common/LineBreaksBox';
import FadeSlide from '@/components/common/FadeSlide';
import { DatabaseLogTableInfo } from '@/types/analytics';
import { showNewlines } from '@/util/text';
import { ContentCopy } from '@mui/icons-material';
import { Dialog, DialogContent, DialogContentText, IconButton, Stack } from '@mui/material';
import { FC, useState } from 'react';

type DatabaseLogInfoProps = {
  log: DatabaseLogTableInfo;
  onClose: () => void;
};

const DatabaseLogInfo: FC<DatabaseLogInfoProps> = ({ log, onClose }) => {
  const [copySuccessful, setCopySuccessful] = useState<boolean>(false);

  const copyValue = (value?: string) => {
    if (value) {
      navigator.clipboard.writeText(showNewlines(value));
    } else {
      const timestamp = log.timestamp ? `Timestamp:\n${showNewlines(log.timestamp)}` : '';
      const performance = log.performance ? `\n\nExecution time:\n${showNewlines(log.performance)}` : '';
      const query = log.query ? `\n\nQuery:\n${showNewlines(log.query)}` : '';
      const response = log.response ? `\n\nResponse\n${showNewlines(log.response)}` : '';
      const returnValue = log.returnValue ? `\n\nReturn value:\n${showNewlines(log.returnValue)}` : '';
      const error = log.error ? `\n\nError:\n${showNewlines(log.error)}` : '';
      const fullValue = timestamp + performance + query + response + returnValue + error;

      navigator.clipboard.writeText(fullValue);
    }
    setCopySuccessful(true);
  };

  return (
    <Dialog open={Boolean(log)} onClose={onClose}>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <DialogContentText>Database Query</DialogContentText>
          <IconButton sx={{ ml: 1 }} onClick={() => copyValue()} color='inherit' size='small'>
            <ContentCopy />
          </IconButton>
        </Stack>
        <Stack direction='column' border={'1px solid'} borderRadius={1} px={1}>
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <DialogContentText>Timestamp</DialogContentText>
            <IconButton sx={{ ml: 1 }} onClick={() => copyValue(log.timestamp)} color='inherit' size='small'>
              <ContentCopy />
            </IconButton>
          </Stack>
          <TextWithLineBreaks>{log.timestamp}</TextWithLineBreaks>
        </Stack>
        <Stack direction='column' border={'1px solid'} borderRadius={1} px={1}>
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <DialogContentText>Performance</DialogContentText>
            <IconButton sx={{ ml: 1 }} onClick={() => copyValue(log.performance)} color='inherit' size='small'>
              <ContentCopy />
            </IconButton>
          </Stack>
          <TextWithLineBreaks>{log.performance}</TextWithLineBreaks>
        </Stack>
        <Stack direction='column' border={'1px solid'} borderRadius={1} px={1}>
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <DialogContentText>Query</DialogContentText>
            <IconButton sx={{ ml: 1 }} onClick={() => copyValue(log.query)} color='inherit' size='small'>
              <ContentCopy />
            </IconButton>
          </Stack>
          <TextWithLineBreaks>{log.query}</TextWithLineBreaks>
        </Stack>
        <Stack direction='column' border={'1px solid'} borderRadius={1} px={1}>
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <DialogContentText>Response</DialogContentText>
            <IconButton sx={{ ml: 1 }} onClick={() => copyValue(log.response)} color='inherit' size='small'>
              <ContentCopy />
            </IconButton>
          </Stack>
          <TextWithLineBreaks>{log.response}</TextWithLineBreaks>
        </Stack>
        {log.returnValue && (
          <Stack direction='column' border={'1px solid'} borderRadius={1} px={1}>
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
              <DialogContentText>Return value</DialogContentText>
              <IconButton sx={{ ml: 1 }} onClick={() => copyValue(log.returnValue)} color='inherit' size='small'>
                <ContentCopy />
              </IconButton>
            </Stack>
            <TextWithLineBreaks>{log.returnValue}</TextWithLineBreaks>
          </Stack>
        )}
        {log.error && (
          <Stack direction='column' border={'1px solid'} borderRadius={1} px={1}>
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
              <DialogContentText>Error</DialogContentText>
              <IconButton sx={{ ml: 1 }} onClick={() => copyValue(log.error)} color='inherit' size='small'>
                <ContentCopy />
              </IconButton>
            </Stack>
            <TextWithLineBreaks>{log.error}</TextWithLineBreaks>
          </Stack>
        )}
      </DialogContent>
      {copySuccessful && (
        <Stack
          position='absolute'
          top={0}
          width='100%'
          height='100%'
          justifyContent='start'
          alignItems='center'
          sx={{ pointerEvents: 'none' }}
        >
          <FadeSlide message={`Copied value to clipboard!`} onDone={() => setCopySuccessful(false)} />
        </Stack>
      )}
    </Dialog>
  );
};

export default DatabaseLogInfo;
